import { useApolloClient, useLazyQuery, useSubscription } from '@apollo/client';
import { getToolOrderStatus, OrderStatusType } from '@bechtel/ht-clients-common';
import {
  OperatorType,
  ToolOrder,
  ToolOrderStatsInput,
  ToolOrderStatsResponse,
  ToolOrderSubscriptionPayload,
  ToolOrderUpdatedSubscriptionPayload,
} from '@bechtel/ht-types';
import { useEffect } from 'react';
import { TOOL_ORDER_CREATED_SUBSCRIPTION, TOOL_ORDER_UPDATED_SUBSCRIPTION } from '~/gql';
import { GET_TOOL_ORDER_STATS, SEARCH_TOOL_ORDER, TOOL_ORDER_COUNT_FOR_SEARCH } from '~/gql/tool-order';

export function useToolOrderUpdateSubscription() {
  const [searchToolOrders, { data: toolOrderData }] = useLazyQuery<{
    toolOrder: ToolOrder;
  }>(SEARCH_TOOL_ORDER, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [GetStats, { data, loading }] = useLazyQuery<
    { toolOrderStats: ToolOrderStatsResponse },
    { toolOrderStatsInput: ToolOrderStatsInput }
  >(GET_TOOL_ORDER_STATS);
  const [getToolOrderCount, { data: orderCountData }] = useLazyQuery<{
    toolOrdersCountForSearch: number;
  }>(TOOL_ORDER_COUNT_FOR_SEARCH, { fetchPolicy: 'network-only' });
  const client = useApolloClient();
  const { data: payLoadData } = useSubscription<{
    toolOrderUpdated: ToolOrderUpdatedSubscriptionPayload;
  }>(TOOL_ORDER_UPDATED_SUBSCRIPTION);

  const { data: createdOrderPayLoadData } = useSubscription<{
    toolOrderCreated: ToolOrderSubscriptionPayload;
  }>(TOOL_ORDER_CREATED_SUBSCRIPTION);

  useEffect(() => {
    if (!payLoadData) return;
    client.cache.evict({ fieldName: 'searchToolOrders' });
    client.cache.evict({ fieldName: 'toolOrderStats' });
    client.cache.evict({ fieldName: 'toolOrdersCountForSearch' });
    client.cache.gc();
  }, [client, payLoadData]);

  useEffect(() => {
    if (!payLoadData) return;
    searchToolOrders({
      variables: {
        searchInput: {
          filterInput: {
            and: [
              {
                field: 'id',
                operator: OperatorType.EQUAL,
                value: payLoadData.toolOrderUpdated.id,
              },
            ],
          },
          sortInput: [],
        },
      },
    });
    GetStats({
      variables: {
        toolOrderStatsInput: { toolRoomId: payLoadData.toolOrderUpdated.toolRoomId },
      },
    });
    getToolOrderCount({
      variables: {
        searchInput: {
          filterInput: {
            and: [
              {
                field: 'status',
                operator: OperatorType.IN,
                values: getToolOrderStatus(
                  OrderStatusType[
                    payLoadData.toolOrderUpdated
                      .toolOrderStatus as keyof typeof payLoadData.toolOrderUpdated.toolOrderStatus
                  ]
                ),
              },
              {
                field: 'toolRoomId',
                operator: OperatorType.EQUAL,
                value: payLoadData.toolOrderUpdated.toolRoomId,
              },
            ],
          },
          sortInput: [],
        },
      },
    });
  }, [payLoadData, GetStats, getToolOrderCount, searchToolOrders]);

  useEffect(() => {
    if (!createdOrderPayLoadData) return;
    client.cache.evict({ fieldName: 'searchToolOrders' });
    client.cache.evict({ fieldName: 'toolOrderStats' });
    client.cache.evict({ fieldName: 'toolOrdersCountForSearch' });
    client.cache.gc();
  }, [client, createdOrderPayLoadData]);
  useEffect(() => {
    if (!createdOrderPayLoadData) return;
    searchToolOrders({
      variables: {
        searchInput: {
          filterInput: {
            and: [
              {
                field: 'id',
                operator: OperatorType.EQUAL,
                value: createdOrderPayLoadData?.toolOrderCreated.id,
              },
            ],
          },
          sortInput: [],
        },
      },
    });
    GetStats({
      variables: {
        toolOrderStatsInput: { toolRoomId: createdOrderPayLoadData?.toolOrderCreated.toolRoomId },
      },
    });
  }, [createdOrderPayLoadData, GetStats, searchToolOrders]);
}
